import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import AtomicEdu from "@/components/AtomicEdu";
import AtomLoaderPreview from "@/pages/AtomLoaderPreview";
import Dashboard from "@/pages/Dashboard";
import LessonWorkspace from "@/pages/LessonWorkspace";
import Admin from "@/pages/Admin";
import RenderPreview from "@/pages/RenderPreview";
import IndividualStudentPlan from "@/pages/IndividualStudentPlan";
import UnitOverlay from "@/pages/UnitOverlay";
import ClassroomSupportHub from "@/pages/classroom-support/Hub";
import ClassroomSupportADHD from "@/pages/classroom-support/ADHD";
import ClassroomSupportGiftedness from "@/pages/classroom-support/Giftedness";
import ClassroomSupportAutism from "@/pages/classroom-support/Autism";
import ClassroomSupport2e from "@/pages/classroom-support/TwiceExceptional";
import ClassroomSupportGenerator from "@/pages/classroom-support/Generator";
import SpecSheet from "@/pages/SpecSheet";
import AuthCallback from "@/pages/AuthCallback";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import SkillsPage from "@/pages/Skills";
import ThePath from "@/pages/ThePath";
import OntarioReportCards from "@/pages/OntarioReportCards";
import AccountSettings from "@/pages/AccountSettings";
import GeneratorPage from "@/pages/GeneratorPage";
import MathEnginePage from "@/pages/MathEngine";
import LongRangePlan from "@/pages/LongRangePlan";
import PlannerDayNotes from "@/pages/PlannerDayNotes";
import PlannerPaDay from "@/pages/PlannerPaDay";
import PlannerPeriod from "@/pages/PlannerPeriod";
import Gradebook from "@/pages/Gradebook";
import TestBuilder from "@/pages/TestBuilder";
import TestMarking from "@/pages/TestMarking";
import BuildSummative from "@/pages/BuildSummative";
import StandaloneSummativeBuilder from "@/pages/StandaloneSummativeBuilder";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import DailyIndex from "@/pages/DailyIndex";
import DailyActivity from "@/pages/DailyActivity";
import TryPage from "@/pages/Try";
import FreeLessonGeneratorPage from "@/pages/FreeLessonGeneratorPage";
import PostersPage from "@/pages/Posters";
import WhySkillsPage from "@/pages/WhySkills";
import AiForOntarioTeachersPage from "@/pages/AiForOntarioTeachers";
import SupportToolsPage from "@/pages/SupportTools";
import ReflectionPage from "@/pages/Reflection";
import ReflectionResultPage from "@/pages/ReflectionResult";
import MyLesson from "@/pages/MyLesson";
import FreeLessonRender from "@/pages/FreeLessonRender";
import MyFreeLessons from "@/pages/MyFreeLessons";
import FeaturedLessonDelivery from "@/pages/FeaturedLessonDelivery";
import SupplyPlansLanding from "@/pages/SupplyPlansLanding";
import SupplyPlanFreeDelivery from "@/pages/SupplyPlanFreeDelivery";
import Samples from "@/pages/Samples";
import LinksPage from "@/pages/Links";
import BannerProbe from "@/pages/BannerProbe";  // dev-only — kept for future Phase 104 banner regression checks
import SiteFooter from "@/components/SiteFooter";
import SiteGate from "@/components/SiteGate";
import ScrollToTop from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { AnalyticsIdentify } from "@/lib/analytics";
import ResumeCheckout from "@/components/ResumeCheckout";
import MobileStickyCta from "@/components/MobileStickyCta";
import { withShell } from "@/components/shell/AppShell";
import "@/lib/clarity";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// We synchronously route to the auth callback whenever the URL fragment carries
// session_id=... — this avoids the race condition the integration playbook warns about.
const AppRouter = () => {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<AtomicEdu />} />
        <Route path="/dev/atom-loader" element={<AtomLoaderPreview />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/lesson/:id" element={withShell(LessonWorkspace, { title: "Lesson" })()} />
        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/why-skills" element={<WhySkillsPage />} />
        <Route path="/ai-for-ontario-teachers" element={<AiForOntarioTeachersPage />} />
        <Route path="/the-path" element={<ThePath />} />
        <Route path="/ontario-report-cards" element={<OntarioReportCards />} />
        <Route path="/account" element={withShell(AccountSettings, { title: "Account" })()} />
        <Route path="/generator" element={withShell(GeneratorPage, { title: "Generator" })()} />
        <Route path="/math-engine" element={withShell(MathEnginePage, { title: "Math Engine" })()} />
        <Route path="/long-range-plan" element={<LongRangePlan />} />
        <Route path="/planner/day/:date" element={withShell(PlannerDayNotes, { title: "Day Notes" })()} />
        <Route path="/planner/pa-day/:date" element={withShell(PlannerPaDay, { title: "PA Day" })()} />
        <Route path="/planner/period/:date/:period" element={withShell(PlannerPeriod, { title: "Period" })()} />
        <Route path="/individual-student-plan" element={withShell(IndividualStudentPlan, { title: "Individual Student Plan" })()} />
        <Route path="/individualize-unit" element={withShell(UnitOverlay, { title: "Individualize this unit" })()} />
        <Route path="/classroom-support" element={<ClassroomSupportHub />} />
        <Route path="/classroom-support/adhd" element={<ClassroomSupportADHD />} />
        <Route path="/classroom-support/giftedness" element={<ClassroomSupportGiftedness />} />
        <Route path="/classroom-support/autism" element={<ClassroomSupportAutism />} />
        <Route path="/classroom-support/2e" element={<ClassroomSupport2e />} />
        <Route path="/classroom-support/:profile/generator" element={<ClassroomSupportGenerator />} />
        <Route path="/tests/new" element={<TestBuilder />} />
        <Route path="/tests/:testId" element={<TestBuilder />} />
        <Route path="/tests/:testId/mark" element={<TestMarking />} />
        <Route path="/assess/build" element={<BuildSummative />} />
        <Route path="/assess/build/performance-task" element={<StandaloneSummativeBuilder />} />
        <Route path="/assess/build/project" element={<StandaloneSummativeBuilder />} />
        <Route path="/gradebook" element={withShell(Gradebook, { title: "Gradebook" })()} />
        <Route path="/admin" element={withShell(Admin, { title: "Admin" })()} />
        <Route path="/admin/render-preview" element={withShell(RenderPreview, { title: "Admin · Render Preview" })()} />
        <Route path="/admin/spec-sheet" element={withShell(SpecSheet, { title: "Admin · Spec Sheet" })()} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/daily" element={<DailyIndex />} />
        <Route path="/daily/:slug" element={<DailyActivity />} />
        <Route path="/try" element={<Navigate to="/lesson-from-resource" replace />} />
        <Route path="/lesson-from-resource" element={<TryPage />} />
        <Route path="/free-lesson-generator" element={<FreeLessonGeneratorPage />} />
        <Route path="/posters" element={<PostersPage />} />
        <Route path="/support-tools" element={<SupportToolsPage />} />
        <Route path="/reflection" element={<ReflectionPage />} />
        <Route path="/reflection/:token" element={<ReflectionResultPage />} />
        <Route path="/samples" element={<Samples />} />
        <Route path="/links" element={<LinksPage />} />
        <Route path="/my-lesson/:token" element={<MyLesson />} />
        <Route path="/free-lesson/:token" element={<FreeLessonRender />} />
        <Route path="/my-free-lessons" element={withShell(MyFreeLessons, { title: "My Free Lessons" })()} />
        <Route path="/featured-lesson/:token" element={<FeaturedLessonDelivery />} />
        <Route path="/supply-plans" element={<SupplyPlansLanding />} />
        <Route path="/supply-plans/deliver/:token" element={<SupplyPlanFreeDelivery />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/__banner_probe" element={<BannerProbe />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SiteFooter />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <SiteGate>
          <AuthProvider>
            <AnalyticsIdentify />
            <ResumeCheckout />
            <AppRouter />
            <MobileStickyCta />
          </AuthProvider>
        </SiteGate>
      </BrowserRouter>
    </div>
  );
}

export default App;
